import { useState, useRef, useEffect, useCallback } from "react";
import { Shield, ChevronDown, X, Eye, EyeOff, CheckCircle2, KeyRound } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { Capacitor } from "@capacitor/core";
import { getSignInOptions, apiServerHost } from "../lib/sailpoint";
import { RegisterTenantForm } from "../components/RegisterTenantForm";
import { Field, Input, PrimaryButton, Spinner, ConfirmModal } from "../components/ui";
import pkg from "../../package.json";
import { normalizeTenantInput } from "../lib/tenantHost";
import { getUrlTenantCandidate } from "../lib/deepLink";

const APP_VERSION = pkg.version;

// The pseudo-tenant slug that selects local admin sign-in from the dropdown.
// Not a real SailPoint org — picking it swaps the form to username/password.
const LOCAL_ADMIN_SLUG = "local-admin";

// Native <datalist> is invisible/unreliable in Safari (no arrow, spotty
// suggestion behavior), so this renders its own always-consistent dropdown
// instead — still a plain text input underneath, so any new tenant can be typed.
function TenantCombobox({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  // Opening the list via the arrow means "show me everything", whereas typing
  // means "narrow it down". Without the distinction, any text already in the
  // field — including a tenant just picked — hides most of the list.
  const [browsing, setBrowsing] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // options is [{ tenant, siteName }] — matched and displayed by siteName
  // (the friendly label), but a click still fills the field with the real
  // tenant slug, since that's what actually gets signed into.
  const filtered =
    browsing || !value
      ? options
      : options.filter(
          (t) => t.siteName.toLowerCase().includes(value.toLowerCase()) || t.tenant.toLowerCase().includes(value.toLowerCase())
        );

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setBrowsing(false); // typing narrows the list
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="your-org"
        autoCapitalize="none"
        autoCorrect="off"
      />
      {value ? (
        // Once something's in the field, the arrow becomes an X that clears
        // it — one tap to start over instead of hand-deleting the slug.
        <button
          type="button"
          onClick={() => {
            onChange("");
            setBrowsing(false);
            setOpen(false);
          }}
          tabIndex={-1}
          aria-label="Clear tenant"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <X size={16} />
        </button>
      ) : options.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setBrowsing(true); // the arrow always reveals every tenant
            setOpen((v) => !v);
          }}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
        >
          <ChevronDown size={16} />
        </button>
      )}
      {open && filtered.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden max-h-48 overflow-y-auto">
          {filtered.map((t) => (
            <li key={t.tenant}>
              <button
                type="button"
                onClick={() => {
                  onChange(t.tenant);
                  setBrowsing(false);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-baseline gap-2"
              >
                <span>{t.siteName}</span>
                {t.siteName !== t.tenant && (
                  <span className="text-xs text-gray-400">{t.tenant}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function LoginPage() {
  const { beginLogin, beginLocalLogin, loading, error: authError } = useAuth();
  const [tenant, setTenant] = useState("");
  const [error, setError] = useState("");
  // Tenants that actually have a sign-in client registered, from the server —
  // the authoritative list, and the same on every device. A locally
  // remembered list could offer tenants this server can't sign into, or omit
  // ones a colleague set up.
  const [tenants, setTenants] = useState([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);
  // Whether the server has a local admin account configured (env-driven) —
  // gates the Local Admin entry in the dropdown.
  const [localAdminEnabled, setLocalAdminEnabled] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [notice, setNotice] = useState("");
  // Set to the candidate tenant string when a URL-named tenant doesn't match
  // any registered one — drives the "Invalid tenant" dialog offering to
  // register it, rather than just silently pre-filling the field.
  const [invalidTenant, setInvalidTenant] = useState(null);
  // Native (iOS) only: the login card flips to the tenant-registration
  // form so a brand-new tenant can be set up right on the device. The
  // web app keeps this behind the Local Admin screen.
  const [addingTenant, setAddingTenant] = useState(false);

  // Read once — a bare useState initializer, not an effect, so it can't
  // pick up a query string change after mount (there's no router on this
  // page to notify it of one anyway).
  const [urlTenantCandidate] = useState(getUrlTenantCandidate);
  const autoLoginAttempted = useRef(false);
  // "pending" until the tenant list resolves one way or the other — shows a
  // plain redirect screen instead of the picker so it never flashes on
  // screen first for a deep link that's about to skip it anyway.
  const [autoLoginPhase, setAutoLoginPhase] = useState(urlTenantCandidate ? "pending" : "none");

  const refreshTenants = useCallback(async () => {
    try {
      const { tenants: list, localAdmin } = await getSignInOptions();
      setTenants(list);
      setLocalAdminEnabled(localAdmin === true);
    } catch {
      // Not fatal — the field is still free text, and an unregistered tenant
      // gets a clear "isn't set up for sign-in yet" on submit.
    } finally {
      setTenantsLoaded(true);
    }
  }, []);

  useEffect(() => {
    refreshTenants();
  }, [refreshTenants]);

  // Deep-link bypass: once the registered-tenant list has loaded, a
  // ?tenant=... or bare ?your-org query param that matches one of them
  // skips the picker entirely and goes straight into sign-in — same
  // beginLogin the Continue button uses, just triggered automatically
  // instead of waiting for a click. A candidate that doesn't match any
  // registered tenant pre-fills the field AND surfaces the Invalid tenant
  // dialog, rather than silently doing nothing about it.
  useEffect(() => {
    if (!urlTenantCandidate || !tenantsLoaded || autoLoginAttempted.current) return;
    autoLoginAttempted.current = true;
    // ?tenant=local-admin just pre-selects the credential form — there's a
    // password to type, so there's nothing to auto-redirect to.
    if (urlTenantCandidate === LOCAL_ADMIN_SLUG) {
      setTenant(LOCAL_ADMIN_SLUG);
      setAutoLoginPhase("none");
      return;
    }
    const match = tenants.find((t) => t.tenant.toLowerCase() === urlTenantCandidate);
    if (!match) {
      setTenant(urlTenantCandidate);
      setInvalidTenant(urlTenantCandidate);
      setAutoLoginPhase("none");
      return;
    }
    setTenant(match.tenant);
    setAutoLoginPhase("redirecting");
    beginLogin(match.tenant).catch((err) => {
      setError(err.message);
      setAutoLoginPhase("none");
    });
  }, [urlTenantCandidate, tenantsLoaded, tenants, beginLogin]);

  // The Local Admin entry rides along in the same combobox as real tenants;
  // selecting it swaps the form below to username/password.
  const isLocalAdmin = tenant.trim().toLowerCase() === LOCAL_ADMIN_SLUG;
  const comboOptions = localAdminEnabled
    ? [...tenants, { tenant: LOCAL_ADMIN_SLUG, siteName: "Local Admin" }]
    : tenants;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (isLocalAdmin) {
      if (!adminUsername.trim() || !adminPassword) {
        setError("Enter the local admin username and password.");
        return;
      }
      try {
        await beginLocalLogin(adminUsername.trim(), adminPassword);
      } catch (err) {
        setError(err.message);
      }
      return;
    }
    // A pasted default-domain address still collapses to the short name; any
    // other value containing a "." is a full-host tenant and is kept whole —
    // that whole string is what it was registered under.
    const t = normalizeTenantInput(tenant);
    if (!t) {
      setError("Enter your tenant name.");
      return;
    }
    try {
      await beginLogin(t);
    } catch (err) {
      setError(err.message);
    }
  }

  if (autoLoginPhase !== "none") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 py-12">
        <Spinner size={24} />
        <p className="text-sm text-gray-500 mt-4">Signing in to {urlTenantCandidate}…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
            <Shield size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Studio</h1>
          <p className="text-sm text-gray-500 mt-1 text-center">
            Connect to your SailPoint ISC tenant
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          {addingTenant ? (
            <div>
              <h2 className="text-sm font-semibold text-gray-900 px-5 pt-1">Set up a new tenant</h2>
              <p className="text-xs text-gray-500 px-5 mt-1">
                Uses a tenant admin Personal Access Token once to create this app's sign-in client. The PAT is never stored.
              </p>
              <RegisterTenantForm
                onDone={(t) => {
                  setAddingTenant(false);
                  setTenant(t);
                  setNotice(`Tenant "${t}" registered — sign in to continue`);
                  refreshTenants();
                }}
              />
              <button
                type="button"
                onClick={() => setAddingTenant(false)}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-700 pb-2"
              >
                Cancel
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <Field label="Tenant name">
                <TenantCombobox value={tenant} onChange={setTenant} options={comboOptions} />
                <p className="text-xs text-gray-400 mt-1">
                  Just the org name — not the full URL
                </p>
              </Field>

              {isLocalAdmin && (
                <>
                  <Field label="Username">
                    <Input
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                      placeholder="Local admin username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      autoComplete="username"
                    />
                  </Field>
                  <Field label="Password">
                    <div className="relative">
                      <Input
                        type={showAdminPassword ? "text" : "password"}
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="Local admin password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowAdminPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        tabIndex={-1}
                      >
                        {showAdminPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </Field>
                </>
              )}

              {notice && (
                <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 text-sm text-emerald-700 flex items-start gap-2">
                  <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                  <span>{notice}</span>
                </div>
              )}

              {/* authError covers failures that happen after we've navigated
                  away — notably the native flow, where the redirect back can
                  fail with no form submission to attach an error to. */}
              {(error || authError) && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-700">
                  {error || authError}
                </div>
              )}

              <PrimaryButton type="submit" loading={loading}>
                {isLocalAdmin ? (
                  <>
                    <KeyRound size={16} />
                    {loading ? "Signing in…" : "Sign in as Local Admin"}
                  </>
                ) : (
                  loading ? "Redirecting…" : "Continue to SailPoint"
                )}
              </PrimaryButton>

              {Capacitor.isNativePlatform() ? (
                <button
                  type="button"
                  onClick={() => { setError(""); setNotice(""); setAddingTenant(true); }}
                  className="mt-3 w-full text-center text-xs text-blue-600 hover:text-blue-700"
                >
                  Set up a new tenant…
                </button>
              ) : (
                <p className="mt-3 text-center text-xs text-gray-400">
                  New tenants are set up by an administrator (Local Admin sign-in)
                </p>
              )}
            </form>
          )}
        </div>

        {/* Help text */}
        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-800 mb-2">Signing in</p>
          <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
            <li>Enter your organization's tenant name</li>
            <li>Sign in on SailPoint's own login page</li>
            <li>Your access mirrors your ISC permissions</li>
          </ol>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Uses OAuth 2.0 authorization code (v2026 API)
          <br />
          You sign in with SailPoint — this app never sees your password
        </p>
        <p className="text-center text-xs text-gray-400 mt-2">Server: {apiServerHost()}</p>
        <p className="text-center text-xs text-gray-500 mt-1">v{APP_VERSION}</p>
      </div>

      {invalidTenant && (
        <ConfirmModal
          title="Invalid tenant specified"
          message={`"${invalidTenant}" isn't set up for sign-in yet. An administrator can register it after signing in as Local Admin.`}
          confirmLabel="OK"
          onConfirm={() => setInvalidTenant(null)}
          onCancel={() => setInvalidTenant(null)}
        />
      )}
    </div>
  );
}
